import { shallowRef, watch } from 'vue'
import {
    PageAccessLevel,
    pageAccessAllows,
    type PageAccessCapability,
} from 'models'
import type { RegisteredHostTool } from 'composables'
import {
    createPageTools,
    type DeclaredPage,
    type PagePresence,
    type PageToolsTranslate,
} from '~/pageTools'
import { discoverFormTools } from '~/pageTools/forms'
import {
    describePointed,
    elementAt,
    pointedFrom,
    pointerLabel,
    selectionOnPage,
} from '~/pageTools/pointing'
import { PageRefs, selectorsAreValid } from '~/pageTools/snapshot'

/**
 * The page tools of the widget, for the agent's effective page access (spec
 * `page-tools.md`). The fixed tools are rebuilt only when the capability
 * changes, never on a change of language: a new registry starts new refs, and
 * the model would lose the ones its last snapshot gave it. The annotated
 * forms are read from the page each time they are asked for. `presence`
 * shows where the agent acts.
 */
export function usePageTools(
    capability: () => PageAccessCapability | undefined,
    t: PageToolsTranslate,
    presence?: PagePresence,
) {
    const registry = shallowRef<RegisteredHostTool[]>([])
    /** The pages the app declared with `registerPages`, read at each call. */
    const pages = shallowRef<DeclaredPage[]>([])
    /** The refs of the tools, and of what the user points at: one registry, so a ref a message carries works. */
    let refs = new PageRefs()
    watch(
        () => JSON.stringify(capability() ?? null),
        () => {
            const current = capability()
            refs = new PageRefs()
            registry.value = current
                ? createPageTools(current, {
                      t,
                      pages: () => pages.value,
                      presence,
                      refs,
                  })
                : []
        },
        { immediate: true },
    )
    /** The rules of the page tools now: none apply while they are off. */
    const rules = () => ({
        excludeSelectors: capability()?.excludeSelectors ?? [],
    })
    /**
     * What the user may point at, and how the message carries it (spec
     * `page-point-and-choose.md`, P): only while the page tools are on,
     * since the model reads it as it reads the page.
     */
    const pointing = {
        /** The element a gesture at a point of the window stands for, if any. */
        at: (x: number, y: number) => {
            const element =
                registry.value.length > 0 ? elementAt(x, y) : undefined
            return element ? pointedFrom(element, rules()) : undefined
        },
        /** What the message carries of `element`, with the selected text if any. */
        describe: (element: Element, selected?: string) =>
            describePointed(element, refs, rules(), selected),
        /** The text selected on the page now, if it may go to the agent. */
        selection: () =>
            registry.value.length > 0 ? selectionOnPage(rules()) : undefined,
        /** What the box on the page calls `element`. */
        label: (element: Element) => pointerLabel(element, rules()),
    }
    const forms = (): RegisteredHostTool[] => {
        const current = capability()
        const rules = { excludeSelectors: current?.excludeSelectors ?? [] }
        if (
            !current ||
            !pageAccessAllows(current.level, PageAccessLevel.Forms) ||
            !selectorsAreValid(rules)
        ) {
            return []
        }
        return discoverFormTools(rules, {
            t,
            confirmEveryAction: current.confirmEveryAction,
        })
    }
    /**
     * Keeps the pages an app declares (spec `page-tools-improvements.md`,
     * M6): the ones with a path of this site, 200 at most.
     */
    const registerPages = (declared: unknown) => {
        pages.value = (Array.isArray(declared) ? declared : [])
            .filter(
                (page): page is DeclaredPage =>
                    typeof (page as DeclaredPage | null)?.path === 'string' &&
                    (page as DeclaredPage).path.startsWith('/'),
            )
            .slice(0, 200)
            .map(({ path, title }) => ({
                path,
                title:
                    typeof title === 'string' ? title.slice(0, 200) : undefined,
            }))
    }
    return { registry, forms, registerPages, pointing }
}
